import React, { FC, useContext, useMemo, useRef, useState } from 'react';
import {
    Box, Button, CircularProgress, CssBaseline,
    Grid, IconButton, InputAdornment, Link, Paper, Radio, Stack, SvgIcon,
    Tab, Tabs, TextField, ThemeProvider,
    Typography, createTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AuthContext from '../../../contexts/authContext';

import jwtInstance from '../../../guards/jwt.interceptor';
import { API_AUTH } from '../../../utils/restApi';
import { errorResponse, successResponse } from '../../../utils/getResponse';
import { compressImage, fileToDataURL } from '../../../utils/compressImage';

export interface UploadedImage {
    dataURL: string;
    file: File;
    originalSizeMB: number;
    compressedSizeMB: number;
    wasCompressed: boolean;
}

const BRAND = '#E0121E';
const INK = '#171922';

const PW_RULES = [
    { label: 'Minim 8 caractere', test: (p: string) => p.length >= 8 },
    { label: 'O cifră (0–9)', test: (p: string) => /\d/.test(p) },
    { label: 'O literă mare (A–Z)', test: (p: string) => /[A-Z]/.test(p) },
    { label: 'Un simbol (@#$%!)', test: (p: string) => /[@#$%!]/.test(p) },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Screen = 'login' | 'register' | 'forgot' | 'reset' | 'verify';

interface AuthPageProps {
    initialScreen?: Screen;
}

const Eye: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12.5a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z" />
    </SvgIcon>
);
const EyeOff: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M12 7a5 5 0 015 5c0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM12 17a5 5 0 01-5-5c0-.43.06-.85.16-1.25l1.62 1.62c.07.86.76 1.55 1.62 1.62l1.62 1.62c-.4.1-.82.16-1.25.16z" />
    </SvgIcon>
);
const Upload: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14a6 6 0 006 6h13a5 5 0 005-5c0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
    </SvgIcon>
);
const Check: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </SvgIcon>
);
const FieldOk: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4.5-4.5 1.41-1.41L10 13.67l7.09-7.09L18.5 8l-8.5 8.5z" />
    </SvgIcon>
);
const Back: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </SvgIcon>
);
const MailSent: FC<any> = (p) => (
    <SvgIcon {...p}>
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </SvgIcon>
);

const AuthPage: FC<AuthPageProps> = ({ initialScreen = 'login' }) => {
    const { login } = useContext(AuthContext);
    const [searchParams] = useSearchParams();

    const theme = useMemo(() => createTheme({
        palette: {
            primary: { main: BRAND, dark: '#B60E18', contrastText: '#fff' },
            secondary: { main: INK },
            background: { default: '#F3F3F5', paper: '#FFFFFF' },
            text: { primary: INK, secondary: '#6B7080' },
        },
        shape: { borderRadius: 12 },
        typography: {
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
            h5: { fontFamily: '"Oswald", sans-serif', fontWeight: 600, letterSpacing: '0.01em' },
            button: { fontFamily: '"Oswald", sans-serif', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
        },
        components: {
            MuiTextField: { defaultProps: { variant: 'outlined', fullWidth: true } },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        background: '#F7F7F9',
                        '& fieldset': { borderColor: '#E6E6EC' },
                        '&:hover fieldset': { borderColor: '#CFCFD8' },
                        '&.Mui-focused': { background: '#fff' },
                        '&.Mui-focused fieldset': { borderWidth: 2 },
                    },
                },
            },
            MuiInputLabel: { defaultProps: { shrink: true }, styleOverrides: { root: { fontWeight: 500 } } },
            MuiButton: { styleOverrides: { root: { borderRadius: 12 } } },
        },
    }), []);

    const [screen, setScreen] = useState<Screen>(initialScreen);
    const [loading, setLoading] = useState(false);
    const [accountType, setAccountType] = useState(searchParams.get('role') || 'ROLE_MANAGER');
    const [showPw, setShowPw] = useState<Record<string, boolean>>({});
    const [regStep, setRegStep] = useState(0);

    // Login form
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Register form
    const [regData, setRegData] = useState({
        lastName: '', firstName: '', email: searchParams.get('email') || '',
        phoneNumber: '', dateOfBirth: '', cnp: '',
        password: '', confirmPassword: '',
    });
    const [profileImages, setProfileImages] = useState<UploadedImage[]>([]);
    const [ciImages, setCiImages] = useState<UploadedImage[]>([]);
    const [regErrors, setRegErrors] = useState<Record<string, string>>({});
    const [regValidated, setRegValidated] = useState(false);
    const [authErrors, setAuthErrors] = useState<Record<string, string>>({});
    const [authValidated, setAuthValidated] = useState(false);

    // Forgot / Reset
    const [forgotEmail, setForgotEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

    // Verify account
    const [verifyCode, setVerifyCode] = useState('');

    const sessionID = useRef(uuidv4());

    const toggle = (k: string) => () => setShowPw((s) => ({ ...s, [k]: !s[k] }));

    const clearError = (key: string) => {
        if (regErrors[key]) setRegErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };

    const OK_SX = {
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50 !important', borderWidth: '2px !important' },
        '& .MuiInputLabel-root:not(.Mui-focused)': { color: '#2E7D32' },
    } as const;

    const isOk = (key: string, value: string) => regValidated && !regErrors[key] && value.trim() !== '';

    const clearAuthError = (key: string) => {
        if (authErrors[key]) setAuthErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };

    const authIsOk = (key: string, value: string) => authValidated && !authErrors[key] && value.trim() !== '';

    const authOkProps = (key: string, value: string) => authIsOk(key, value)
        ? {
            error: false as const,
            helperText: undefined as string | undefined,
            sx: OK_SX,
            InputProps: {
                endAdornment: (
                    <InputAdornment position="end">
                        <FieldOk sx={{ fontSize: 20, color: '#4CAF50' }} />
                    </InputAdornment>
                ),
            },
        }
        : { error: !!authErrors[key], helperText: authErrors[key] };

    const okProps = (key: string, value: string) => isOk(key, value)
        ? {
            error: false,
            helperText: undefined as string | undefined,
            sx: OK_SX,
            InputProps: {
                endAdornment: (
                    <InputAdornment position="end">
                        <FieldOk sx={{ fontSize: 20, color: '#4CAF50' }} />
                    </InputAdornment>
                ),
            },
        }
        : { error: !!regErrors[key], helperText: regErrors[key] };

    const goTo = (s: Screen) => () => {
        setLoading(false);
        setAuthErrors({});
        setAuthValidated(false);
        setScreen(s);
        if (s !== 'register') setRegStep(0);
    };

    // --- Validation helpers ---

    const validateLogin = (): boolean => {
        const e: Record<string, string> = {};
        setAuthValidated(true);
        if (!loginEmail.trim()) e.loginEmail = 'Câmp obligatoriu';
        else if (!EMAIL_RE.test(loginEmail)) e.loginEmail = 'Adresă de email invalidă';
        if (!loginPassword) e.loginPassword = 'Câmp obligatoriu';
        setAuthErrors(e);
        return !Object.keys(e).length;
    };

    const validateForgot = (): boolean => {
        const e: Record<string, string> = {};
        setAuthValidated(true);
        if (!forgotEmail.trim()) e.forgotEmail = 'Câmp obligatoriu';
        else if (!EMAIL_RE.test(forgotEmail)) e.forgotEmail = 'Adresă de email invalidă';
        setAuthErrors(e);
        return !Object.keys(e).length;
    };

    const validateReset = (pwAllMet: boolean): boolean => {
        const e: Record<string, string> = {};
        setAuthValidated(true);
        if (!resetCode.trim()) e.resetCode = 'Câmp obligatoriu';
        if (!newPassword) e.newPassword = 'Câmp obligatoriu';
        else if (!pwAllMet) e.newPassword = 'Parola nu îndeplinește toate cerințele';
        if (!newPasswordConfirm) e.newPasswordConfirm = 'Câmp obligatoriu';
        else if (newPassword !== newPasswordConfirm) e.newPasswordConfirm = 'Parolele nu coincid';
        setAuthErrors(e);
        return !Object.keys(e).length;
    };

    const validateVerify = (): boolean => {
        const e: Record<string, string> = {};
        setAuthValidated(true);
        if (!verifyCode.trim()) e.verifyCode = 'Câmp obligatoriu';
        setAuthErrors(e);
        return !Object.keys(e).length;
    };

    // --- Submit handlers ---

    const handleLogin = async () => {
        if (!validateLogin() || loading) return;
        setLoading(true);
        try {
            await login({ email: loginEmail, password: loginPassword });
        } catch (_) {
            // errorResponse already called in authContext
        }
        setLoading(false);
    };

    const handleRegister = async () => {
        if (loading) return;
        setLoading(true);
        const profileDataURL = profileImages[0]?.dataURL ?? '';
        const ciDataURL = ciImages[0]?.dataURL ?? '';
        try {
            const response = await jwtInstance.post(
                API_AUTH.register,
                {
                    nameOfRole: accountType,
                    ...regData,
                    profileImage: profileDataURL,
                    identityCard: ciDataURL,
                },
                { params: { invitationCode: searchParams.get('invitationCode') } }
            );
            if (response.status === 200) {
                successResponse(response, 'Înregistrare cont', 'success');
                setScreen('verify');
            }
        } catch (error: any) {
            errorResponse(error, 'Eroare înregistrare', 'danger');
        }
        setLoading(false);
    };

    const handleForgot = async () => {
        if (!validateForgot() || loading) return;
        setLoading(true);
        try {
            const response = await jwtInstance.get(API_AUTH.resetPassword, {
                params: { email: forgotEmail, sessionID: sessionID.current },
            });
            if (response.status === 200) {
                successResponse(response, 'Resetare parolă', 'default');
                setScreen('reset');
            }
        } catch (error: any) {
            errorResponse(error, 'Eroare resetare parolă', 'danger');
        }
        setLoading(false);
    };

    const handleReset = async () => {
        if (!validateReset(resetPwAllMet) || loading) return;
        setLoading(true);
        try {
            const response = await jwtInstance.post(API_AUTH.forgottenPassword, {
                sixDigitCode: resetCode.toUpperCase(),
                password: newPassword,
                confirmPassword: newPasswordConfirm,
            });
            if (response.status === 200) {
                successResponse(response, 'Resetare parolă', 'default');
                setScreen('login');
            }
        } catch (error: any) {
            errorResponse(error, 'Eroare resetare parolă', 'danger');
        }
        setLoading(false);
    };

    const handleVerify = async () => {
        if (!validateVerify() || loading) return;
        setLoading(true);
        try {
            const response = await jwtInstance.get(API_AUTH.verify, {
                params: { emailToken: verifyCode.toUpperCase() },
            });
            if (response.status === 200) {
                successResponse(response, 'Verificare cont', 'success');
                setScreen('login');
            }
        } catch (error: any) {
            errorResponse(error, 'Eroare verificare cont', 'danger');
        }
        setLoading(false);
    };

    // --- Shared UI ---

    const Logo = (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box component="img" src="/logo.svg" alt="Victory Cup" sx={{ width: 150, objectFit: 'contain' }} />
        </Box>
    );

    const head = (title: string, sub: string, center = false) => (
        <Box sx={{ textAlign: center ? 'center' : 'left', mb: 3 }}>
            <Typography variant="h5" sx={{ fontSize: 26, color: 'text.primary' }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: center ? 320 : 'none', mx: center ? 'auto' : 0 }}>
                {sub}
            </Typography>
        </Box>
    );

    const pwField = (label: string, key: string, placeholder: string, value: string, onChange: (v: string) => void, errorMsg?: string, showOk?: boolean) => (
        <TextField
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            type={showPw[key] ? 'text' : 'password'}
            error={!!errorMsg}
            helperText={errorMsg}
            sx={showOk ? OK_SX : undefined}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {showOk && <FieldOk sx={{ fontSize: 20, color: '#4CAF50', mr: 0.5 }} />}
                        <IconButton onClick={toggle(key)} edge="end" size="small" aria-label="comută vizibilitatea parolei">
                            {showPw[key] ? <EyeOff fontSize="small" /> : <Eye fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    const primaryBtn = (label: string, onClick: () => void) => (
        <Button
            variant="contained" fullWidth size="large" disableElevation
            onClick={onClick} disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{
                mt: 3, py: 1.4, fontSize: 15,
                boxShadow: '0 14px 26px -14px rgba(224,18,30,.65)',
                '&:hover': { boxShadow: '0 16px 30px -14px rgba(224,18,30,.75)' },
            }}
        >
            {loading ? 'Se procesează…' : label}
        </Button>
    );

    const Dropzone: FC<{
        images: UploadedImage[];
        setImages: (imgs: UploadedImage[]) => void;
        accept: string[];
        maxMB: number;
        maxWidthOrHeight?: number;
    }> = ({ images, setImages, accept, maxMB, maxWidthOrHeight }) => {
        const [compressing, setCompressing] = useState(false);
        const [typeError, setTypeError] = useState(false);
        const inputRef = useRef<HTMLInputElement>(null);
        const img = images[0];

        const ACCEPTED_MIME: Record<string, string> = {
            jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
        };
        const acceptedMimes = accept.map((ext) => ACCEPTED_MIME[ext] ?? `image/${ext}`);

        const handleFile = async (file: File) => {
            setTypeError(false);
            if (!acceptedMimes.includes(file.type)) {
                setTypeError(true);
                return;
            }
            setCompressing(true);
            try {
                const originalSizeMB = file.size / (1024 * 1024);
                const compressed = await compressImage(file, { maxSizeMB: maxMB, maxWidthOrHeight });
                const dataURL = await fileToDataURL(compressed);
                const compressedSizeMB = compressed.size / (1024 * 1024);
                setImages([{ dataURL, file: compressed, originalSizeMB, compressedSizeMB, wasCompressed: compressed.size < file.size }]);
            } catch (_) {
                // fall back to original file if compression fails
                const dataURL = await fileToDataURL(file);
                const sizeMB = file.size / (1024 * 1024);
                setImages([{ dataURL, file, originalSizeMB: sizeMB, compressedSizeMB: sizeMB, wasCompressed: false }]);
            }
            setCompressing(false);
        };

        const openPicker = () => inputRef.current?.click();

        const onDrop = (e: React.DragEvent) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handleFile(file);
        };

        return (
            <Box>
                <input
                    ref={inputRef} type="file" style={{ display: 'none' }}
                    accept={acceptedMimes.join(',')}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                />
                <Box
                    onClick={!img && !compressing ? openPicker : undefined}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    sx={{
                        border: '2px dashed',
                        borderColor: img ? 'primary.main' : '#D7D7DF',
                        borderRadius: 3, p: 2.5, textAlign: 'center',
                        cursor: img || compressing ? 'default' : 'pointer',
                        bgcolor: img ? 'rgba(224,18,30,.04)' : '#FAFAFB',
                        transition: 'all .18s',
                        '&:hover': !img && !compressing ? { borderColor: 'primary.main', bgcolor: '#fff' } : {},
                    }}
                >
                    {compressing ? (
                        <Stack alignItems="center" spacing={1}>
                            <CircularProgress size={28} color="primary" />
                            <Typography variant="body2" color="text.secondary">Se optimizează imaginea…</Typography>
                        </Stack>
                    ) : img ? (
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                                component="img" src={img.dataURL} alt=""
                                sx={{ width: 52, height: 52, borderRadius: 2, objectFit: 'cover', border: '1px solid #eee', flexShrink: 0 }}
                            />
                            <Box sx={{ textAlign: 'left', overflow: 'hidden', flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {img.file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {img.wasCompressed
                                        ? `${img.originalSizeMB.toFixed(1)} MB → ${img.compressedSizeMB.toFixed(2)} MB (optimizat)`
                                        : `${img.compressedSizeMB.toFixed(2)} MB`}
                                </Typography>
                                <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                                    <Typography variant="caption" color="primary.main" sx={{ cursor: 'pointer' }} onClick={openPicker}>
                                        Schimbă
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', cursor: 'pointer' }} onClick={() => setImages([])}>
                                        Șterge
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    ) : (
                        <Stack alignItems="center" spacing={0.75}>
                            <Upload sx={{ fontSize: 32, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Trage fișierul aici sau{' '}
                                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>răsfoiește</Box>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {accept.map((e) => e.toUpperCase()).join(', ')} · max {maxMB} MB (imaginile mari sunt comprimate automat)
                            </Typography>
                        </Stack>
                    )}
                </Box>
                {typeError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        Format neacceptat. Formate valide: {accept.join(', ')}
                    </Typography>
                )}
            </Box>
        );
    };

    // --- Screen tabs ---
    const showTabs = screen === 'login' || screen === 'register';
    const tabs = (
        <Tabs
            value={screen === 'register' ? 1 : 0}
            onChange={(_, v) => { if (!loading) setScreen(v === 0 ? 'login' : 'register'); }}
            variant="fullWidth"
            sx={{
                bgcolor: '#F1F1F4', borderRadius: 2.5, p: 0.5, minHeight: 0, mb: 1,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTab-root': {
                    minHeight: 42, borderRadius: 2, textTransform: 'none',
                    fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 600, fontSize: 14.5,
                    color: 'text.secondary', transition: 'all .2s',
                },
                '& .Mui-selected': { bgcolor: INK, color: '#fff !important', boxShadow: '0 3px 8px rgba(0,0,0,.2)' },
            }}
        >
            <Tab disableRipple label="Autentificare" />
            <Tab disableRipple label="Înregistrare" />
        </Tabs>
    );

    // --- Login screen ---
    const loginScreen = (
        <Box>
            {head('Bine ai revenit', 'Autentifică-te ca să continui în Victory Cup.')}
            <Stack spacing={2}>
                <TextField
                    label="Adresa de email" type="email" placeholder="nume@email.com"
                    value={loginEmail}
                    {...authOkProps('loginEmail', loginEmail)}
                    onChange={(e) => { setLoginEmail(e.target.value); clearAuthError('loginEmail'); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                />
                {pwField('Parola', 'login', 'Introdu parola', loginPassword,
                    (v) => { setLoginPassword(v); clearAuthError('loginPassword'); },
                    authErrors.loginPassword,
                    authIsOk('loginPassword', loginPassword),
                )}
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                <Link
                    component="button" type="button" onClick={goTo('forgot')}
                    sx={{ fontSize: 14, fontWeight: 600, color: 'primary.main', textDecoration: 'none' }}
                >
                    Ai uitat parola?
                </Link>
            </Box>
            {primaryBtn('Intră în cont', handleLogin)}
        </Box>
    );

    // --- Register screen ---
    const accountTypes = [
        { v: 'ROLE_MANAGER', l: 'Manager', d: 'Administrezi o echipă' },
        { v: 'ROLE_DELEGATE', l: 'Delegat', d: 'Reprezinți clubul la meciuri' },
        { v: 'ROLE_PLAYER', l: 'Jucător', d: 'Ești parte din lot' },
    ];
    const regStepLabels = ['Tip cont', 'Date personale', 'Securitate', 'Documente'];
    const lastStep = regStepLabels.length - 1;

    const filteredAccountTypes = searchParams.get('role')
        ? accountTypes.filter((t) => t.v === searchParams.get('role'))
        : accountTypes;

    const pwAllMet = PW_RULES.every((r) => r.test(regData.password));
    const resetPwAllMet = PW_RULES.every((r) => r.test(newPassword));

    const buildPwIndicator = (pw: string) => pw.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 0.75, pl: 0.5 }}>
            {PW_RULES.map((r) => {
                const met = r.test(regData.password);
                return (
                    <Stack key={r.label} direction="row" alignItems="center" spacing={0.75}>
                        <SvgIcon sx={{ fontSize: 13, color: met ? '#2E7D32' : '#CFCFD8', flexShrink: 0 }}>
                            {met
                                ? <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                : <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />}
                        </SvgIcon>
                        <Typography variant="caption" sx={{ color: met ? '#2E7D32' : 'text.secondary', lineHeight: 1.3 }}>
                            {r.label}
                        </Typography>
                    </Stack>
                );
            })}
        </Stack>
    );

    const validateStep = (): boolean => {
        const errors: Record<string, string> = {};
        setRegValidated(true);

        if (regStep === 1) {
            if (!regData.lastName.trim()) errors.lastName = 'Câmp obligatoriu';
            if (!regData.firstName.trim()) errors.firstName = 'Câmp obligatoriu';
            if (!regData.email.trim()) errors.email = 'Câmp obligatoriu';
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regData.email)) errors.email = 'Adresă de email invalidă';
            if (!regData.phoneNumber.trim()) errors.phoneNumber = 'Câmp obligatoriu';
        }

        if (regStep === 2) {
            if (!regData.dateOfBirth) {
                errors.dateOfBirth = 'Câmp obligatoriu';
            } else if (dayjs().diff(dayjs(regData.dateOfBirth), 'year') < 18) {
                errors.dateOfBirth = 'Trebuie să ai cel puțin 18 ani';
            }
            if (!regData.cnp.trim()) errors.cnp = 'Câmp obligatoriu';
            if (!regData.password) errors.password = 'Câmp obligatoriu';
            else if (!pwAllMet) errors.password = 'Parola nu îndeplinește toate cerințele';
            if (!regData.confirmPassword) errors.confirmPassword = 'Câmp obligatoriu';
            else if (regData.password !== regData.confirmPassword) errors.confirmPassword = 'Parolele nu coincid';
        }

        if (regStep === 3) {
            if (!profileImages.length) errors.profileImage = 'Fotografia de profil este obligatorie';
            if (!ciImages.length) errors.ciImage = 'Cartea de identitate este obligatorie';
        }

        setRegErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const regStepContent = [
        (
            <Stack key='step-0' spacing={1.25}>
                {filteredAccountTypes.map((t) => (
                    <Box
                        key={t.v} onClick={() => setAccountType(t.v)}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 2.5, cursor: 'pointer',
                            border: '2px solid', borderColor: accountType === t.v ? 'primary.main' : '#E6E6EC',
                            bgcolor: accountType === t.v ? 'rgba(224,18,30,.05)' : '#fff', transition: 'all .15s',
                        }}
                    >
                        <Radio
                            checked={accountType === t.v} size="small"
                            sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                        />
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{t.l}</Typography>
                            <Typography variant="body2" color="text.secondary">{t.d}</Typography>
                        </Box>
                    </Box>
                ))}
            </Stack>
        ),
        (
            <Grid container key='step-1' spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField label="Nume de familie" placeholder="Ex: Popescu"
                        value={regData.lastName}
                        {...okProps('lastName', regData.lastName)}
                        onChange={(e) => { setRegData((d) => ({ ...d, lastName: e.target.value })); clearError('lastName'); }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField label="Prenume" placeholder="Ex: Alexandru"
                        value={regData.firstName}
                        {...okProps('firstName', regData.firstName)}
                        onChange={(e) => { setRegData((d) => ({ ...d, firstName: e.target.value })); clearError('firstName'); }} />
                </Grid>
                <Grid item xs={12}>
                    <TextField label="Adresa de email" type="email" placeholder="nume@email.com"
                        value={regData.email}
                        {...okProps('email', regData.email)}
                        onChange={(e) => { setRegData((d) => ({ ...d, email: e.target.value })); clearError('email'); }} />
                </Grid>
                <Grid item xs={12}>
                    <TextField label="Număr de telefon" type="tel" placeholder="Ex: 0765 553 333"
                        value={regData.phoneNumber}
                        {...okProps('phoneNumber', regData.phoneNumber)}
                        onChange={(e) => { setRegData((d) => ({ ...d, phoneNumber: e.target.value })); clearError('phoneNumber'); }} />
                </Grid>
            </Grid>
        ),
        (
            <Grid container key='step-2' spacing={2}>
                <Grid item xs={12} sm={6}>
                    <DatePicker
                        label="Data nașterii"
                        value={regData.dateOfBirth ? dayjs(regData.dateOfBirth) : null}
                        onChange={(val: Dayjs | null) => {
                            setRegData((d) => ({ ...d, dateOfBirth: val ? val.format('YYYY-MM-DD') : '' }));
                            clearError('dateOfBirth');
                        }}
                        views={['year', 'month', 'day']}
                        maxDate={dayjs().subtract(18, 'year')}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                variant: 'outlined',
                                placeholder: 'ZZ/LL/AAAA',
                                error: !!regErrors.dateOfBirth,
                                helperText: regErrors.dateOfBirth,
                                ...(isOk('dateOfBirth', regData.dateOfBirth) ? { sx: OK_SX } : {}),
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField label="Cod numeric personal" placeholder="Introduceți CNP"
                        value={regData.cnp}
                        {...okProps('cnp', regData.cnp)}
                        onChange={(e) => { setRegData((d) => ({ ...d, cnp: e.target.value })); clearError('cnp'); }} />
                </Grid>
                <Grid item xs={12}>
                    {pwField('Parola', 'reg', 'Minim 8 caractere, o cifră, o majusculă, un simbol',
                        regData.password,
                        (v) => { setRegData((d) => ({ ...d, password: v })); clearError('password'); },
                        regErrors.password,
                        isOk('password', regData.password) && pwAllMet,
                    )}
                    {buildPwIndicator(regData.password)}
                </Grid>
                <Grid item xs={12}>
                    {pwField('Confirmare parolă', 'reg2', 'Repetă parola',
                        regData.confirmPassword,
                        (v) => { setRegData((d) => ({ ...d, confirmPassword: v })); clearError('confirmPassword'); },
                        regErrors.confirmPassword,
                        isOk('confirmPassword', regData.confirmPassword) && regData.password === regData.confirmPassword,
                    )}
                </Grid>
            </Grid>
        ),
        (
            <Stack key='step-3' spacing={2.5}>
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14.5, mb: 0.25 }}>Fotografie de profil</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>JPG sau PNG, maxim 1 MB.</Typography>
                    <Dropzone images={profileImages} setImages={setProfileImages} accept={['jpg', 'jpeg', 'png']} maxMB={1} maxWidthOrHeight={800} />
                    {regErrors.profileImage && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{regErrors.profileImage}</Typography>
                    )}
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14.5, mb: 0.25 }}>Carte de identitate</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Fotografie clară a C.I. (maxim 4 MB). Dacă cerințele nu sunt îndeplinite, contul nu va fi activat.
                    </Typography>
                    <Dropzone images={ciImages} setImages={setCiImages} accept={['jpg', 'jpeg', 'png']} maxMB={4} maxWidthOrHeight={2048} />
                    {regErrors.ciImage && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{regErrors.ciImage}</Typography>
                    )}
                </Box>
            </Stack>
        ),
    ];

    const regNext = () => {
        if (!validateStep()) return;
        setRegErrors({});
        setRegValidated(false);
        if (regStep < lastStep) setRegStep((s) => s + 1);
        else handleRegister();
    };
    const regBack = () => {
        setRegErrors({});
        setRegValidated(false);
        if (regStep > 0) setRegStep((s) => s - 1);
        else setScreen('login');
    };

    const registerScreen = (
        <Box>
            {head('Înregistrare cont', `Pasul ${regStep + 1} din ${regStepLabels.length} · ${regStepLabels[regStep]}`)}
            <Stack direction="row" spacing={0.75} sx={{ mb: 3 }}>
                {regStepLabels.map((s, i) => (
                    <Box key={s} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: i <= regStep ? 'primary.main' : '#E6E6EC' }} />
                ))}
            </Stack>
            <Box key={regStep} sx={{ minHeight: 248, animation: 'vcfade .3s cubic-bezier(.2,.7,.2,1) both' }}>
                {regStepContent[regStep]}
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button
                    variant="outlined" onClick={regBack} startIcon={<Back fontSize="small" />}
                    disabled={loading}
                    sx={{
                        flex: '0 0 auto', py: 1.4, px: 2.5, borderColor: '#DADAE0', color: 'text.secondary',
                        fontFamily: '"Plus Jakarta Sans", sans-serif', textTransform: 'none', fontWeight: 600,
                        '&:hover': { borderColor: '#BDBDC6', bgcolor: '#FAFAFB' },
                    }}
                >
                    {regStep === 0 ? 'Anulează' : 'Înapoi'}
                </Button>
                <Button
                    variant="contained" fullWidth size="large" disableElevation
                    onClick={regNext} disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                    sx={{ py: 1.4, fontSize: 15, boxShadow: '0 14px 26px -14px rgba(224,18,30,.65)' }}
                >
                    {regStep < lastStep ? 'Continuă' : (loading ? 'Se procesează…' : 'Înregistrează cont')}
                </Button>
            </Stack>
        </Box>
    );

    // --- Forgot password screen ---
    const forgotScreen = (
        <Box>
            {head('Ai uitat parola?', 'Introdu adresa de email și îți trimitem un cod de resetare.')}
            <TextField
                label="Adresa de email" type="email" placeholder="nume@email.com"
                value={forgotEmail}
                {...authOkProps('forgotEmail', forgotEmail)}
                onChange={(e) => { setForgotEmail(e.target.value); clearAuthError('forgotEmail'); }}
                onKeyDown={(e) => e.key === 'Enter' && handleForgot()}
            />
            {primaryBtn('Trimite codul', handleForgot)}
            <Button
                variant="text" fullWidth startIcon={<Back fontSize="small" />} onClick={goTo('login')}
                disabled={loading}
                sx={{ mt: 1.25, color: 'text.secondary', fontFamily: '"Plus Jakarta Sans", sans-serif', textTransform: 'none', fontWeight: 600 }}
            >
                Înapoi la autentificare
            </Button>
        </Box>
    );

    // --- Reset password screen ---
    const resetScreen = (
        <Box>
            {head('Setează o parolă nouă', 'Introdu codul primit pe email și alege o parolă sigură.')}
            <Stack spacing={2}>
                <TextField
                    label="Codul primit pe email" placeholder="Ex: A1B2C3"
                    value={resetCode}
                    {...authOkProps('resetCode', resetCode)}
                    onChange={(e) => { setResetCode(e.target.value); clearAuthError('resetCode'); }}
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                    onKeyDown={(e) => e.key === 'Enter' && handleReset()}
                />
                <Box>
                    {pwField('Parola nouă', 'new', 'Minim 8 caractere, o cifră, o majusculă, un simbol',
                        newPassword,
                        (v) => { setNewPassword(v); clearAuthError('newPassword'); },
                        authErrors.newPassword,
                        authIsOk('newPassword', newPassword) && resetPwAllMet,
                    )}
                    {buildPwIndicator(newPassword)}
                </Box>
                {pwField('Confirmă parola', 'newc', 'Repetă parola',
                    newPasswordConfirm,
                    (v) => { setNewPasswordConfirm(v); clearAuthError('newPasswordConfirm'); },
                    authErrors.newPasswordConfirm,
                    authIsOk('newPasswordConfirm', newPasswordConfirm) && newPassword === newPasswordConfirm,
                )}
            </Stack>
            {primaryBtn('Salvează parola', handleReset)}
            <Button
                variant="text" fullWidth startIcon={<Back fontSize="small" />} onClick={goTo('forgot')}
                disabled={loading}
                sx={{ mt: 1.25, color: 'text.secondary', fontFamily: '"Plus Jakarta Sans", sans-serif', textTransform: 'none', fontWeight: 600 }}
            >
                Înapoi
            </Button>
        </Box>
    );

    // --- Verify account screen ---
    const verifyScreen = (
        <Box sx={{ textAlign: 'center', py: 1 }}>
            <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 3 }}>
                <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    bgcolor: 'rgba(224,18,30,.10)', animation: 'vcpulse 2s ease-out infinite',
                }} />
                <Box sx={{
                    position: 'relative', width: 96, height: 96, borderRadius: '50%',
                    bgcolor: 'rgba(224,18,30,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <MailSent sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
            </Box>
            {head('Verificare cont', 'Introdu codul de activare primit pe adresa ta de email.', true)}
            <TextField
                label="Codul primit pe email"
                placeholder="Ex: A1B2C3"
                value={verifyCode}
                error={!!authErrors.verifyCode}
                helperText={authErrors.verifyCode}
                {...(authIsOk('verifyCode', verifyCode) ? { sx: { ...OK_SX, mb: 0 } } : { sx: { mb: 0 } })}
                onChange={(e) => { setVerifyCode(e.target.value); clearAuthError('verifyCode'); }}
                onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                inputProps={{ style: { textTransform: 'uppercase', textAlign: 'center', fontSize: 20, letterSpacing: '0.2em' } }}
            />
            {primaryBtn('Activează contul', handleVerify)}
            <Button
                variant="text" fullWidth onClick={goTo('login')}
                disabled={loading}
                sx={{ mt: 1, color: 'text.secondary', fontFamily: '"Plus Jakarta Sans", sans-serif', textTransform: 'none', fontWeight: 600 }}
            >
                Înapoi la autentificare
            </Button>
        </Box>
    );

    const screens: Record<Screen, JSX.Element> = {
        login: loginScreen,
        register: registerScreen,
        forgot: forgotScreen,
        reset: resetScreen,
        verify: verifyScreen,
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
                @keyframes vcpulse { 0%{transform:scale(.7);opacity:.7} 80%{transform:scale(1.6);opacity:0} 100%{opacity:0} }
                @keyframes vcfade { from{transform:translateY(10px);opacity:0} to{transform:none;opacity:1} }
            `}</style>
            <Box sx={{ position: 'relative', minHeight: '100vh', width: '100%', overflow: 'hidden', bgcolor: '#fff', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>

                {/* ── Background ── */}
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
                    {/* Dark base + glow accents */}
                    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#241016,#0e0c11 65%)' }}>
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, background: 'radial-gradient(58% 55% at 72% 20%, rgba(255,120,90,.30), transparent 60%), radial-gradient(50% 60% at 86% 82%, rgba(224,18,30,.24), transparent 65%)' }} />
                        {/* fade.webp replaces image-slot */}
                        <Box
                            component="img"
                            src="/assets/fade.webp"
                            alt=""
                            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2, display: 'block' }}
                        />
                    </Box>
                    {/* White fade: full on mobile, pulls left on desktop */}
                    <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 3,
                        background: {
                            xs: 'linear-gradient(180deg, rgba(255,255,255,.72), rgba(255,255,255,.97))',
                            md: 'linear-gradient(100deg, #fff 0%, #fff 24%, rgba(255,255,255,.96) 36%, rgba(255,255,255,.5) 54%, rgba(255,255,255,0) 72%)',
                        },
                    }} />
                    {/* Soft red glow behind card */}
                    <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 4,
                        background: {
                            xs: 'radial-gradient(60% 40% at 50% 36%, rgba(224,18,30,.10), transparent 70%)',
                            md: 'radial-gradient(46% 62% at 30% 46%, rgba(224,18,30,.12), transparent 72%), radial-gradient(30% 42% at 18% 16%, rgba(255,150,120,.10), transparent 70%)',
                        },
                    }} />
                </Box>

                {/* ── Content: card anchored left ── */}
                <Box sx={{
                    position: 'relative', zIndex: 1,
                    minHeight: '100vh',
                    display: 'flex', flexDirection: 'column',
                    alignItems: { xs: 'center', md: 'flex-start' },
                    justifyContent: 'center',
                    px: { xs: 2.5, sm: 5, md: 9, lg: 13, xl: '9vw' },
                    py: { xs: 5, md: 6 },
                    boxSizing: 'border-box',
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            width: { xs: '100%', sm: screen === 'register' ? 560 : 440, xl: screen === 'register' ? 600 : 480 },
                            maxWidth: '100%',
                            p: { xs: 3, sm: 4, xl: 5 },
                            borderRadius: 4,
                            border: '1px solid rgba(236,236,239,.9)',
                            boxShadow: '0 40px 80px -36px rgba(20,22,30,.34), 0 2px 10px rgba(20,22,30,.06)',
                            transition: 'width .35s cubic-bezier(.2,.7,.2,1)',
                        }}
                    >
                        {Logo}
                        {showTabs && tabs}
                        <Box key={screen} sx={{ mt: showTabs ? 3 : 1, animation: 'vcfade .38s cubic-bezier(.2,.7,.2,1) both' }}>
                            {screens[screen]}
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </ThemeProvider>
        </LocalizationProvider>
    );
};

export default AuthPage;
